/**
 * Client/Customer Name Resolution Utility
 *
 * Provides pure functions for resolving client/customer names.
 * Used across project and budget modules for consistent name display.
 */

import { IProject } from '../../project/types/project'
import { IStudyBudget } from '../types/studyBudget'
import { IClient } from '../../../api/budgetSettingsApi'

/**
 * Resolves project customer name with fallback to category
 *
 * @param project - Project object with optional customer and required category
 * @returns Customer name if exists, otherwise category (therapeutic area)
 *
 * @example
 * // With customer object
 * const project = { customer: { name: 'Acme Pharma' }, category: 'Oncology' }
 * const name = getProjectCustomerName(project)
 * console.log(name) // 'Acme Pharma'
 *
 * @example
 * // Without customer (fallback to category)
 * const project = { customer: undefined, category: 'Oncology' }
 * const name = getProjectCustomerName(project)
 * console.log(name) // 'Oncology'
 *
 * @example
 * // Empty customer name (fallback to category)
 * const project = { customer: { name: '' }, category: 'Cardiology' }
 * const name = getProjectCustomerName(project)
 * console.log(name) // 'Cardiology'
 */
export function getProjectCustomerName(project: IProject): string {
  return project.customer?.name || project.category || ''
}

/**
 * Resolves budget client name by looking up client ID in client list
 *
 * @param budget - Budget object with client ID
 * @param clients - Array of client objects from Redux state
 * @returns Client name
 * @throws Error if client not found
 *
 * @example
 * const budget = { client: 5 }
 * const clients = [
 *   { id: 5, name: 'Pharma Corp', contact: 'john@pharma.com' },
 *   { id: 6, name: 'BioTech Inc', contact: 'jane@biotech.com' }
 * ]
 * const name = getBudgetClientName(budget, clients)
 * console.log(name) // 'Pharma Corp'
 */
export function getBudgetClientName(
  budget: Pick<IStudyBudget, 'client'>,
  clients: IClient[]
): string {
  const client = clients.find(c => c.id === budget.client)
  return client!.name
}

/**
 * Generic client/customer name resolver for both projects and budgets
 *
 * Type guard checks whether input is a project or budget and calls appropriate resolver.
 * For budgets, requires client list to resolve ID to name.
 *
 * @param entity - Project or Budget object
 * @param clients - Array of client objects (required for budgets, ignored for projects)
 * @returns Resolved client/customer name
 *
 * @example
 * // With project
 * const project = { customer: { name: 'Acme' }, category: 'Oncology' }
 * const name = getClientName(project, [])
 * console.log(name) // 'Acme'
 *
 * @example
 * // With budget
 * const budget = { client: 5, category: 'Neurology' }
 * const clients = [{ id: 5, name: 'Pharma Corp', contact: 'john@pharma.com' }]
 * const name = getClientName(budget, clients)
 * console.log(name) // 'Pharma Corp'
 */
export function getClientName(entity: IProject | IStudyBudget, clients: IClient[]): string {
  if ('customer' in entity) {
    return getProjectCustomerName(entity)
  }
  if ('client' in entity) {
    return getBudgetClientName(entity, clients)
  }
  return ''
}
